/**
 * Google Analytics 4, loaded ONLY after the visitor accepts, and off entirely until GA4_ID is set.
 * Advertising features (Google signals, ad personalization) are disabled, which keeps the Privacy Policy
 * statement "we do not share Personal Data for cross-context behavioral advertising" true.
 */
import { SITE_CONFIG } from '../../config/site-config';
import { isConfigured } from '../../lib/links';

export type AnalyticsParams = Readonly<Record<string, string>>;

const GTAG_URL = 'https://www.googletagmanager.com/gtag/js';

let loaded = false;
const queuedEvents: [string, AnalyticsParams][] = [];

export const isAnalyticsEnabled = (): boolean => isConfigured(SITE_CONFIG.GA4_ID);

/** Records an event. Events before consent are queued and sent if the visitor accepts. */
export function trackEvent(event: string, params: AnalyticsParams = {}): void {
  if (!isAnalyticsEnabled()) return;
  if (loaded && window.gtag) window.gtag('event', event, params);
  else queuedEvents.push([event, params]);
}

export function trackPageView(): void {
  if (loaded && window.gtag)
    window.gtag('event', 'page_view', { page_location: location.href, page_title: document.title });
}

export const isAnalyticsLoaded = (): boolean => loaded;

export function loadAnalytics(): void {
  if (loaded || !isAnalyticsEnabled()) return;
  loaded = true;
  const dataLayer: unknown[] = (window.dataLayer ??= []);
  // gtag.js reads the `arguments` object itself, so the shim must push it unchanged.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params -- gtag.js requires the Arguments object
    dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', SITE_CONFIG.GA4_ID, { allow_google_signals: false, allow_ad_personalization_signals: false });
  const script = document.createElement('script');
  script.async = true;
  script.src = `${GTAG_URL}?id=${encodeURIComponent(SITE_CONFIG.GA4_ID)}`;
  document.head.appendChild(script);
  queuedEvents.splice(0).forEach(([event, params]) => window.gtag?.('event', event, params));
}
